// ServiceSection.js
// Service section for the job dialog

import React, { useState, useEffect, useCallback, useMemo } from 'react'; // Added useCallback, useMemo
import { isDriverOnly } from '../../utils/permissionStore'; // Updated import
import {
  Grid,
 Paper,
 Typography,
 TextField,
 ButtonGroup,
 Button,
 CircularProgress, // Added for loading indicator
 Checkbox,         // Added Checkbox
 FormControlLabel, // Added FormControlLabel
 IconButton,       // Added for temp provider + button
 Tooltip,          // Added for temp provider + button
 Box               // Added for temp provider row layout
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';
import TempProviderDialog from './TempProviderDialog';
import FilterSelect from '../common/FilterSelect';
import {
  serviceOptions,
  classTypeOptions,
   scheduledTimeOptions
 } from '../../constants/dashboardConstants';
 import { fetchRankedSPsForJob } from '../../services/jobDashboardService'; // Import SP fetching
 import { getUserOrganizationLevel } from '../../utils/permissionStore'; // Import correct function
 import useAvailableDrivers from '../../hooks/useAvailableDrivers'; // Import the hook
 import { extractId } from '../../utils/selectUtils'; // Import utility function

/**
 * Service section component for the job dialog
 * @param {Object} props Component props
 * @param {Object} props.jobData Current job data
 * @param {Function} props.handleInputChange Function to handle input changes
 * @param {Array} props.vehicles List of vehicles
 * @param {Object} props.currentUser Current user data
 * @param {Boolean} props.autoAssignPreference Current state of auto-assign preference
 * @param {Function} props.handleAutoAssignChange Function to update auto-assign preference
 * @returns {JSX.Element} Service section component
 */
const ServiceSection = ({ jobData, handleInputChange, vehicles = [], currentUser, autoAssignPreference, handleAutoAssignChange }) => {
  // Check if user is driver-only
  const isDriverOnlyUser = currentUser ? isDriverOnly(currentUser.primaryRole, currentUser.secondaryRoles) : false;
  // State for all vehicles, combining prop and localStorage
  const [allVehicles, setAllVehicles] = useState([]);
  // State for SPs
  const [rankedSPs, setRankedSPs] = useState([]);
  const [loadingSPs, setLoadingSPs] = useState(false);
  // Temporary providers: fetched by zip radius (default 60 mi) plus any
  // created in this dialog session. Manual assignment only — they are
  // excluded from auto-dispatch and blast.
  const [sessionTempProviders, setSessionTempProviders] = useState([]);
  const [nearbyTempProviders, setNearbyTempProviders] = useState([]);
  const [tempRadius, setTempRadius] = useState('60');
  const [loadingTempProviders, setLoadingTempProviders] = useState(false);
  const [tempDialogOpen, setTempDialogOpen] = useState(false);

  const tempProviders = useMemo(() => {
    const byId = new Map();
    [...nearbyTempProviders, ...sessionTempProviders].forEach(tp => byId.set(String(tp.id), tp));
    return [...byId.values()];
  }, [nearbyTempProviders, sessionTempProviders]);

  const jobZipForTemp = jobData?.serviceLocation?.zip;
  const fetchNearbyTempProviders = useCallback(async () => {
    if (!jobZipForTemp || !currentUser || getUserOrganizationLevel(currentUser) !== 1) {
      setNearbyTempProviders([]);
      return;
    }
    setLoadingTempProviders(true);
    try {
      const token = localStorage.getItem('token');
      const radius = parseFloat(tempRadius) || 60;
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/facilities/temporary-provider/nearby`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { zip: jobZipForTemp, radius }
      });
      setNearbyTempProviders(res.data.providers || []);
    } catch (err) {
      console.error('Error fetching nearby temporary providers:', err);
      setNearbyTempProviders([]);
    } finally {
      setLoadingTempProviders(false);
    }
  }, [jobZipForTemp, tempRadius, currentUser]);

  useEffect(() => { fetchNearbyTempProviders(); }, [fetchNearbyTempProviders]);

  // *** Use the hook internally, DO NOT pass jobZip for this component ***
  const {
    availableDrivers: rawAvailableDrivers, // Rename raw data from hook
    isLoading: driversLoading,
    error: driversError
  } = useAvailableDrivers(true, null); // Pass null for jobZip

  // *** Filter drivers based on currentUser's vendor ID or Org Level ***
  const availableDrivers = useMemo(() => {
    // console.log('[ServiceSection useMemo] Running filter. rawAvailableDrivers:', rawAvailableDrivers); // Log input
    if (!currentUser || !rawAvailableDrivers) return [];

    const isCurrentUserOrg1 = getUserOrganizationLevel(currentUser) === 1;

    if (isCurrentUserOrg1) {
        // Org 1 users see all Org 1 drivers returned by the hook (backend already filtered by isOnDuty)
        const org1Drivers = rawAvailableDrivers.filter(d => d.organizationLevel === 1);
        // console.log('[ServiceSection useMemo] Org 1 User - Filtered drivers:', org1Drivers); // Log output
        return org1Drivers;
    } else {
        // SP users filter by their vendor ID
        const userVendorIdentifier = currentUser.vendorId || currentUser.vendorNumber;
        if (!userVendorIdentifier) {
          console.warn("[ServiceSection] SP user missing vendor identifier. No drivers will be shown.");
          return []; // SP user with no vendor ID sees no drivers
        }
        const spDrivers = rawAvailableDrivers.filter(driver => {
            const driverVendorIdentifier = driver.vendorId || driver.vendorNumber;
            return driverVendorIdentifier === userVendorIdentifier;
        });
        // console.log('[ServiceSection useMemo] SP User - Filtered drivers:', spDrivers); // Log output
        return spDrivers;
    }
  }, [rawAvailableDrivers, currentUser]);


  // Get vehicles from localStorage if available
  useEffect(() => {
    const cachedVehicles = localStorage.getItem('fleetVehicles');
    if (cachedVehicles) {
      try {
        const parsedVehicles = JSON.parse(cachedVehicles);
        setAllVehicles(parsedVehicles);
      } catch (e) {
        console.error('Error parsing cached vehicles:', e);
        setAllVehicles(vehicles || []);
      }
    } else {
      setAllVehicles(vehicles || []);
    }
  }, [vehicles]);

  // Fetch ranked SPs when relevant jobData changes
  const fetchSPs = useCallback(async () => {
    const isOrg1Dispatcher = currentUser && getUserOrganizationLevel(currentUser) === 1;
    const hasRequiredData = jobData?.id &&
                            jobData?.serviceLocation?.zip &&
                            jobData?.serviceLocation?.street &&
                            jobData?.serviceLocation?.city &&
                            jobData?.serviceLocation?.state &&
                            jobData?.service &&
                            jobData?.classType;

    // console.log('[ServiceSection fetchSPs] Checking conditions:', { /* ... */ });

    if (isOrg1Dispatcher && hasRequiredData) {
      setLoadingSPs(true);
      try {
        const jobDetailsForRanking = {
          id: jobData.id,
          service: jobData.service,
          classType: jobData.classType,
          serviceLocation: jobData.serviceLocation,
          dropoffLocation: jobData.dropoffLocation
        };
        // console.log('[ServiceSection fetchSPs] Calling fetchRankedSPsForJob with:', JSON.stringify(jobDetailsForRanking));
        const data = await fetchRankedSPsForJob(jobDetailsForRanking);
        // console.log('[ServiceSection fetchSPs] Received ranked SPs:', data);
        setRankedSPs(data);
      } catch (err) {
        console.error("Error fetching ranked SPs in ServiceSection:", err);
        setRankedSPs([]);
      } finally {
        setLoadingSPs(false);
      }
    } else {
      // console.log('[ServiceSection fetchSPs] Conditions not met, clearing SPs.');
      setRankedSPs([]);
    }
  }, [
      jobData?.id,
      jobData?.serviceLocation?.zip,
      jobData?.serviceLocation?.street,
      jobData?.serviceLocation?.city,
      jobData?.serviceLocation?.state,
      jobData?.dropoffLocation,
      jobData?.service,
      jobData?.classType,
      currentUser
  ]);

  useEffect(() => {
    if (jobData?.id && jobData?.serviceLocation?.zip && jobData?.service && jobData?.classType) {
        fetchSPs();
    } else {
        setRankedSPs([]);
    }
  }, [fetchSPs, jobData?.id, jobData?.serviceLocation?.zip, jobData?.service, jobData?.classType]);


  // Extract ID from driverAssigned in case it's an object
  const driverAssignedId = extractId(jobData.driverAssigned);

  // Determine the valid values for each assignment dropdown (mutually exclusive)
  const isSPAssigned = rankedSPs.some(sp => sp.id === driverAssignedId);
  const isTempAssigned = tempProviders.some(tp => String(tp.id) === String(driverAssignedId));
  // Use the filtered availableDrivers list for this check
  const isDriverAssigned = availableDrivers.some(d => d.id === driverAssignedId);
  const driverValue = isDriverAssigned ? driverAssignedId : '';
  const spValue = isSPAssigned ? driverAssignedId : '';
  const tempValue = isTempAssigned ? driverAssignedId : '';

   const orgLevel = currentUser ? getUserOrganizationLevel(currentUser) : null;
   const isOrg1User = orgLevel === 1;

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>Service</Typography>
      <Grid container spacing={2}>
        {/* Service Time, ETA/Scheduled, Service, Class Type Grids... */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>Service Time</Typography>
          <ButtonGroup variant="outlined" fullWidth>
            <Button
              variant={jobData.serviceTime === 'ASAP' ? 'contained' : 'outlined'}
              onClick={() => handleInputChange('serviceTime', 'ASAP')}
            >
              ASAP
            </Button>
            <Button
              variant={jobData.serviceTime === 'Scheduled' ? 'contained' : 'outlined'}
              onClick={() => handleInputChange('serviceTime', 'Scheduled')}
            >
              Scheduled
            </Button>
          </ButtonGroup>
        </Grid>
        <Grid item xs={12}>
          {jobData.serviceTime === 'ASAP' ? (
            <TextField
              margin="dense"
              label="ETA"
              fullWidth
              value={jobData.eta || ''}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[0-9]*$/.test(value)) {
                  handleInputChange('eta', value);
                }
              }}
              inputProps={{ maxLength: 3 }}
            />
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                  <FilterSelect
                    label="Scheduled For"
                    required
                    error={!jobData.scheduledTime}
                    value={jobData.scheduledTime || ''}
                    options={scheduledTimeOptions}
                    onChange={(selectedOption) => {
                      handleInputChange('scheduledTime', selectedOption);
                      if (selectedOption !== 'Custom Date/Time') {
                        const today = new Date();
                        let scheduledDate = today.toLocaleDateString();
                        if (selectedOption === 'Tomorrow') {
                          const tomorrow = new Date();
                          tomorrow.setDate(today.getDate() + 1);
                          scheduledDate = tomorrow.toLocaleDateString();
                        } else if (selectedOption === 'This Week') {
                          const endOfWeek = new Date();
                          const daysToSaturday = 6 - today.getDay();
                          endOfWeek.setDate(today.getDate() + daysToSaturday);
                          scheduledDate = endOfWeek.toLocaleDateString();
                        } else if (selectedOption === 'Next Week') {
                          const nextWednesday = new Date();
                          const daysToNextWednesday = (10 - today.getDay()) % 7 + 3;
                          nextWednesday.setDate(today.getDate() + daysToNextWednesday);
                          scheduledDate = nextWednesday.toLocaleDateString();
                        } else if (selectedOption === 'This Month') {
                          const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                          scheduledDate = endOfMonth.toLocaleDateString();
                        }
                        handleInputChange('scheduledDate', scheduledDate);
                      }
                    }}
                  />
              </Grid>
              {jobData.scheduledTime === 'Custom Date/Time' && (
                <>
                  <Grid item xs={6}>
                    <TextField
                      margin="dense"
                      label="Date (MM/DD/YYYY) *"
                      fullWidth
                      value={jobData.scheduledDate || ''}
                      onChange={(e) => { if (/^[0-9/]*$/.test(e.target.value)) { handleInputChange('scheduledDate', e.target.value); } }}
                      placeholder="MM/DD/YYYY"
                      required
                      error={!jobData.scheduledDate}
                      helperText={!jobData.scheduledDate ? "Required" : ""}
                      inputProps={{ maxLength: 10 }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      margin="dense"
                      label="Time (HH:MM AM/PM or 24hr) *"
                      fullWidth
                      value={jobData.customTime || ''}
                      onChange={(e) => { if (/^[0-9: AMP]*$/.test(e.target.value)) { handleInputChange('customTime', e.target.value); } }}
                      placeholder="HH:MM AM or HH:MM (24hr)"
                      required
                      inputProps={{ maxLength: 8 }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          )}
        </Grid>
        <Grid item xs={12}>
          <FilterSelect
            label="Service"
            required
            error={!jobData.service}
            value={jobData.service || ''}
            options={serviceOptions}
            onChange={(value) => handleInputChange('service', value)}
          />
        </Grid>
        <Grid item xs={12}>
          <FilterSelect
            label="Class Type"
            required
            error={!jobData.classType}
            value={jobData.classType || ''}
            options={classTypeOptions}
            onChange={(value) => handleInputChange('classType', value)}
          />
        </Grid>

        {/* Driver Assigned Dropdown */}
        <Grid item xs={12}>
          <FilterSelect
            label="Assign Driver"
            value={driverValue}
            disabled={isDriverOnlyUser || !!spValue || !!tempValue || driversLoading}
            loading={driversLoading}
            options={availableDrivers.map((driver) => ({
              value: driver.id,
              label: `${driver.name}${driver.status ? ` (${driver.status})` : ''}`,
            }))}
            onChange={(driverId) => {
              handleInputChange('driverAssigned', driverId);
              if (driverId && allVehicles && allVehicles.length > 0) {
                const driverVehicle = allVehicles.find(vehicle => vehicle.driverId === driverId || (vehicle.driver && vehicle.driver.includes(availableDrivers.find(d => d.id === driverId)?.name || '')));
                if (driverVehicle) { handleInputChange('truckAssigned', driverVehicle.name); }
              }
            }}
          />
          {/* Check the filtered list length */}
          {!driversLoading && availableDrivers.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              No on-duty drivers available for your organization.
            </Typography>
          )}
          {driversError && (
             <Typography variant="caption" color="error">
               Error loading drivers: {driversError}
             </Typography>
          )}
        </Grid>

        {/* Service Provider Assigned Dropdown - Conditionally Rendered for Org 1 Users */}
        {isOrg1User && (
          <Grid item xs={12}>
            <FilterSelect
              label="Assign Service Provider"
              value={spValue}
              disabled={isDriverOnlyUser || loadingSPs || !!tempValue || !!(jobData.driverAssigned && !isSPAssigned && !isTempAssigned) /* Disable if an Org 1 driver or temp provider is selected */}
              loading={loadingSPs}
              options={rankedSPs.map((sp) => ({
                value: sp.id,
                label: `${sp.companyName || sp.name} (Dist: ${sp.totalEnRouteDistance ?? 'N/A'} mi` +
                  `${sp.totalLoadedDistance !== null ? ` | Loaded: ${sp.totalLoadedDistance} mi` : ''}` +
                  ` | Est. Cost: $${sp.estimatedTotalCost !== null ? sp.estimatedTotalCost.toFixed(2) : 'N/A'})`,
              }))}
              onChange={(spId) => {
                handleInputChange('driverAssigned', spId);
                handleInputChange('truckAssigned', '');
              }}
            />
            {!loadingSPs && rankedSPs.length === 0 && jobData?.id && (
              <Typography variant="caption" color="text.secondary">
                No eligible Service Providers found for this job's location/time. Try a temporary provider below.
              </Typography>
            )}
          </Grid>
        )}

        {/* Temporary Providers - manual assignment only (never auto-dispatch/blast) */}
        {isOrg1User && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Temporary Providers (manual only)</Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
              <FilterSelect
                label="Assign Temporary Provider"
                value={tempValue}
                disabled={isDriverOnlyUser || !!spValue || !!(jobData.driverAssigned && !isTempAssigned && !isSPAssigned)}
                options={tempProviders.map((tp) => ({
                  value: tp.id,
                  label: `${tp.companyName} (${tp.vendorId}${tp.distance !== undefined ? ` | ${tp.distance} mi` : ''})`,
                }))}
                onChange={(tpId) => {
                  handleInputChange('driverAssigned', tpId);
                  handleInputChange('truckAssigned', '');
                }}
              />
              <TextField
                label="Radius"
                type="number"
                size="small"
                value={tempRadius}
                onChange={(e) => setTempRadius(e.target.value)}
                inputProps={{ min: 1, max: 500 }}
                sx={{ width: 90, mt: 1 }}
                helperText="miles"
              />
              <Tooltip title="Refresh the temporary provider list">
                <span>
                  <IconButton sx={{ mt: 1.5 }} onClick={fetchNearbyTempProviders} disabled={loadingTempProviders} aria-label="Refresh temporary providers">
                    {loadingTempProviders ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Create a temporary service provider (local company reached by phone)">
                <span>
                  <IconButton
                    color="primary"
                    sx={{ mt: 1.5 }}
                    onClick={() => setTempDialogOpen(true)}
                    disabled={isDriverOnlyUser}
                    aria-label="Create temporary service provider"
                  >
                    <AddIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            {!loadingTempProviders && tempProviders.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No temporary providers within {parseFloat(tempRadius) || 60} miles of this job's zip. Use + to create one.
              </Typography>
            )}
            {isTempAssigned && (
              <>
                <Typography variant="caption" color="warning.main" display="block">
                  Temporary provider selected: the job dispatches directly on update (no acceptance step) and the provider is emailed the agreed amount with their login.
                </Typography>
                <TextField
                  fullWidth
                  margin="dense"
                  size="small"
                  type="number"
                  label="Agreed Amount ($) *"
                  value={jobData.invoice?.total ?? ''}
                  onChange={(e) => {
                    const amount = parseFloat(e.target.value);
                    if (!isNaN(amount) && amount > 0) {
                      // Becomes the job invoice; the backend requires it before
                      // dispatching to a temporary provider and will not
                      // auto-recalculate over it.
                      handleInputChange('invoice', {
                        items: [{ item: 'Agreed amount (temporary provider)', qty: 1, price: amount, total: amount }],
                        total: amount,
                        ratesSource: 'manual'
                      });
                    } else {
                      handleInputChange('invoice', null);
                    }
                  }}
                  inputProps={{ min: 0, step: '0.01' }}
                  error={!(jobData.invoice?.total > 0)}
                  helperText={!(jobData.invoice?.total > 0) ? 'Required — the amount agreed on the phone becomes the job invoice.' : 'This amount becomes the job invoice.'}
                />
              </>
            )}
            <TempProviderDialog
              open={tempDialogOpen}
              onClose={() => setTempDialogOpen(false)}
              onCreated={(spUser) => {
                setSessionTempProviders(prev => prev.some(tp => String(tp.id) === String(spUser.id)) ? prev : [...prev, spUser]);
                handleInputChange('driverAssigned', spUser.id);
                handleInputChange('truckAssigned', '');
              }}
            />
          </Grid>
        )}

        {/* Truck Assigned Dropdown */}
        <Grid item xs={12}>
          <FilterSelect
            label="Truck Assigned"
            value={jobData.truckAssigned || ''}
            disabled={isDriverOnlyUser || !!spValue || !!tempValue}
            options={allVehicles && allVehicles.length > 0
              ? allVehicles.map((vehicle) => ({
                  value: vehicle.name,
                  label: `${vehicle.name} - ${vehicle.type}${vehicle.driver ? ` (Assigned to: ${vehicle.driver})` : ''}`,
                }))
              : ['HQ', 'Truck 2']}
            onChange={(value) => handleInputChange('truckAssigned', value)}
          />
           {!!spValue && (
              <Typography variant="caption" color="text.secondary">
                  Truck assignment is handled by the Service Provider.
              </Typography>
           )}
        </Grid>

        {/* Auto-Assign Checkbox - Only show if not editing and not driver only */}
        {!jobData.id && !isDriverOnlyUser && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={autoAssignPreference}
                  onChange={handleAutoAssignChange}
                  name="autoAssign"
                  color="primary"
                  disabled={!!jobData.driverAssigned}
                />
              }
              label="Automatically assign best available Driver/SP upon creation"
            />
             {!!jobData.driverAssigned && (
                <Typography variant="caption" display="block" color="text.secondary" sx={{ pl: 4 }}>
                    (Manual assignment overrides auto-assign)
                </Typography>
             )}
          </Grid>
        )}

      </Grid>
    </Paper>
  );
 };

 export default ServiceSection;
